export type LogCategory =
  | "system"
  | "player"
  | "game_master"
  | "examination"
  | "image"
  | "navigation"
  | "save"
  | "ai"
  | "error";

export type LogLevel = "info" | "warn" | "error" | "debug";

export interface LogEntry {
  seq: number;
  timestamp: string;
  elapsed_ms: number;
  category: LogCategory;
  level: LogLevel;
  event: string;
  data: Record<string, unknown>;
  duration_ms?: number;
}

export interface SessionSummary {
  session_id: string;
  started_at: string;
  ended_at: string | null;
  duration_ms: number;
  total_entries: number;
  entry_counts: Record<LogCategory, number>;
  player_actions: number;
  llm_calls: number;
  images_generated: number;
  examinations: number;
  errors: number;
}

export interface SessionExport {
  version: 1;
  summary: SessionSummary;
  entries: LogEntry[];
}

const MAX_LOG_SIZE = 10 * 1024 * 1024; // 10 MB

export type ValidationResult =
  | { valid: true; log: SessionExport }
  | { valid: false; error: string };

export function validateSessionLog(data: unknown, byteSize?: number): ValidationResult {
  if (byteSize !== undefined && byteSize > MAX_LOG_SIZE) {
    return { valid: false, error: "Session log exceeds the 10 MB size limit." };
  }

  if (typeof data !== "object" || data === null || Array.isArray(data)) {
    return { valid: false, error: "Session log must be a JSON object." };
  }

  const obj = data as Record<string, unknown>;

  if (obj.version !== 1) {
    return { valid: false, error: "Unsupported session log version. Expected version 1." };
  }

  if (typeof obj.summary !== "object" || obj.summary === null) {
    return { valid: false, error: "Session log is missing a valid summary." };
  }

  const summary = obj.summary as Record<string, unknown>;
  if (typeof summary.session_id !== "string" || typeof summary.total_entries !== "number") {
    return {
      valid: false,
      error: "Session summary is missing required fields (session_id, total_entries).",
    };
  }

  if (!Array.isArray(obj.entries)) {
    return { valid: false, error: "Session log entries must be an array." };
  }

  return { valid: true, log: data as SessionExport };
}

export function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
}
